import { ItemClasses } from "../../common/constants"
import { roundToPrecision } from "../../helpers/math"
import { ApplyBudgetData } from "../BudgetCalculator/budgetCalculatorModel"

export type Budget = {
  year: number
  name: string
  displayName: string
  isDefault: boolean
  taxRate: number
  class: number
  overhead: number
  profit: number
}

export const defaultBudget = (): Budget => ({
  year: 0,
  name: '',
  displayName: '',
  isDefault: false,
  taxRate: 0,
  class: ItemClasses.Labor,
  overhead: 0,
  profit: 0
})

export const taxRatePercentToFraction = (percent: number): number => roundToPrecision(percent / 100, 4)

export const taxRateFractionToPercent = (fraction: number): number => roundToPrecision(fraction * 100, 2)

export type ViewModelClass = {
  class: number,
  profit: number
  overhead: number
}

export type ViewModel = {
  name: string
  isDefault: boolean
  taxRate: number
  year: number
  labor: ViewModelClass
  equipment: ViewModelClass
  materials: ViewModelClass
  subcontracting: ViewModelClass
  other: ViewModelClass
}

export const toBudget = (budgets: Budget[], value: ViewModel, row: ViewModelClass): Budget => {
  const budget = budgets.find(x => x.class === row.class)!
  return {
    ...budget,
    name: value.name,
    taxRate: taxRateFractionToPercent(value.taxRate),
    isDefault: value.isDefault,
    profit: roundToPrecision(row.profit * 100, 2),
    overhead: roundToPrecision(row.overhead * 100, 2),
  }
}

export const fromBudget = (budget: Budget): ViewModelClass => {
  return {
    class: budget.class,
    profit: roundToPrecision(budget.profit / 100, 4),
    overhead: roundToPrecision(budget.overhead / 100, 4),
  }
}

export const toViewModel = (budgets: Budget[]): ViewModel => {
  return {
    name: budgets[0].displayName,
    taxRate: taxRatePercentToFraction(budgets[0].taxRate),
    isDefault: budgets[0].isDefault,
    year: budgets[0].year,
    labor: fromBudget(budgets.find(x => x.class === ItemClasses.Labor)!),
    equipment: fromBudget(budgets.find(x => x.class === ItemClasses.Equipment)!),
    materials: fromBudget(budgets.find(x => x.class === ItemClasses.Materials)!),
    subcontracting: fromBudget(budgets.find(x => x.class === ItemClasses.Subcontracting)!),
    other: fromBudget(budgets.find(x => x.class === ItemClasses.Other)!),

  }
}

export const fromViewModel = (budgets: Budget[], viewModel: ViewModel): Budget[] => {
  return [
    toBudget(budgets, viewModel, viewModel.labor),
    toBudget(budgets, viewModel, viewModel.equipment),
    toBudget(budgets, viewModel, viewModel.materials),
    toBudget(budgets, viewModel, viewModel.subcontracting),
    toBudget(budgets, viewModel, viewModel.other),
  ]
}

export const applyCalculatorData = (budgets: Budget[], data: ApplyBudgetData): Budget[] => {
  const overheadByClass: { [itemClass: number]: number } = {
    [ItemClasses.Labor]: data.laborOverhead,
    [ItemClasses.Equipment]: data.equipmentOverhead,
    [ItemClasses.Materials]: data.materialsOverhead,
    [ItemClasses.Subcontracting]: data.subcontractorOverhead,
  }
  const profitClasses: number[] = [ItemClasses.Labor, ItemClasses.Equipment, ItemClasses.Materials, ItemClasses.Subcontracting, ItemClasses.Other]

  return budgets.map(budget => {
    const overhead = overheadByClass[budget.class]
    return {
      ...budget,
      overhead: overhead !== undefined ? roundToPrecision(overhead * 100, 2) : budget.overhead,
      profit: profitClasses.includes(budget.class) ? roundToPrecision(data.profitMargin * 100, 2) : budget.profit,
    }
  })
}
